/**
 * Data Formatting Engine for Mini Printer
 * Handles dates, numbers, decimals, percentages, currency, text transforms, and fallbacks.
 */

import { normalizeHeader } from "./columnMatcher";

export type DataFormatType = "text" | "date" | "number" | "currency" | "percent";

export interface FieldFormattingConfig {
  formatType?: DataFormatType;
  dateFormat?: string;
  decimalPlaces?: number;
  currencySymbol?: string;
  transform?: "none" | "uppercase" | "lowercase" | "trim";
  fallback?: string;
  required?: boolean;
}

export interface FormatDateResult {
  text: string;
  /** True when the original value was kept because it could not be parsed safely. */
  invalid: boolean;
  warning?: string;
}

const DATE_NAME_TOKENS = [
  "ngay",
  "date",
  "ngaygiao",
  "ngaynhan",
  "ngaygiaohang",
  "deliverydate",
  "ngaydukiengiaohangtoikho",
  "ngaydukien",
  "ngaydukiengiaohang",
  "ngaygiaodukien",
  "ngayyeucaugiaohang",
  "ngayhengiaohang",
  "ngaydathang",
  "ngayxacnhan",
  "ngaysx",
  "nsx",
  "hsd",
  "hansd",
  "hansudung",
  "expiry",
  "expire",
  "expdate",
  "mfgdate",
];

/** True when a template/Excel field name looks like a date field. */
export function looksLikeDateField(fieldName: string): boolean {
  const norm = normalizeHeader(fieldName);
  if (!norm) return false;
  if (DATE_NAME_TOKENS.some((t) => norm === t || norm.includes(t))) return true;
  return /^ngay/.test(norm) || /date$/.test(norm) || norm.includes("ngay");
}

/**
 * Parse a calendar date without inventing day/month swaps.
 *
 * Accepted:
 * - Date instance
 * - Excel serial number
 * - ISO `YYYY-MM-DD` (and optional time)
 * - `D/M/YY` or `DD/MM/YYYY` when both parts ≤ 12 — Vietnam DD/MM
 * - Unambiguous US `M/D/YY` (second part > 12) → swapped to calendar date, then DD/MM/YYYY
 */
export function parseDateStrict(val: unknown): { date: Date | null; keptOriginal: boolean; reason?: string } {
  if (val == null || val === "") return { date: null, keptOriginal: false };

  if (val instanceof Date) {
    if (isNaN(val.getTime())) return { date: null, keptOriginal: true, reason: "Date object không hợp lệ" };
    return { date: val, keptOriginal: false };
  }

  if (typeof val === "number") {
    if (!isFinite(val) || val <= 0) {
      return { date: null, keptOriginal: true, reason: "Số serial ngày không hợp lệ" };
    }
    // Excel serial date (1900 system)
    const dateMs = (val - 25569) * 86400 * 1000;
    const date = new Date(dateMs);
    if (isNaN(date.getTime())) {
      return { date: null, keptOriginal: true, reason: "Không chuyển được serial Excel sang ngày" };
    }
    return { date, keptOriginal: false };
  }

  const str = String(val).trim();
  if (!str) return { date: null, keptOriginal: false };

  // ISO YYYY-MM-DD (optionally with time)
  const iso = str.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s].*)?$/);
  if (iso) {
    const y = Number(iso[1]);
    const m = Number(iso[2]);
    const d = Number(iso[3]);
    const date = new Date(y, m - 1, d);
    if (date.getFullYear() !== y || date.getMonth() !== m - 1 || date.getDate() !== d) {
      return { date: null, keptOriginal: true, reason: `Ngày ISO không hợp lệ: ${str}` };
    }
    return { date, keptOriginal: false };
  }

  // D/M/YYYY, DD/MM/YYYY, or unambiguous US M/D/YY (second part > 12).
  const dmy = str.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2}|\d{4})$/);
  if (dmy) {
    let d = Number(dmy[1]);
    let m = Number(dmy[2]);
    let y = Number(dmy[3]);
    if (y < 100) y += 2000;

    // 7/29/26 or 05/25/2026 — second slot cannot be a month, so first is month (US).
    if (m > 12 && d <= 12) {
      const month = d;
      const day = m;
      d = day;
      m = month;
    }
    if (d > 31 || m > 12 || m < 1 || d < 1) {
      return { date: null, keptOriginal: true, reason: `Ngày không hợp lệ: ${str}` };
    }
    const date = new Date(y, m - 1, d);
    if (date.getFullYear() !== y || date.getMonth() !== m - 1 || date.getDate() !== d) {
      return { date: null, keptOriginal: true, reason: `Ngày không hợp lệ: ${str}` };
    }
    return { date, keptOriginal: false };
  }

  // Last resort: Date.parse only for strings that already include a month name or ISO-like form.
  // Avoid bare numeric parse that flips MDY/DMY by locale.
  if (/[a-zA-Z]/.test(str)) {
    const parsed = Date.parse(str);
    if (!isNaN(parsed)) {
      return { date: new Date(parsed), keptOriginal: false };
    }
  }

  return { date: null, keptOriginal: true, reason: `Không parse được ngày: ${str}` };
}

function applyPattern(date: Date, formatPattern: string): string {
  const YYYY = String(date.getFullYear());
  const MM = String(date.getMonth() + 1).padStart(2, "0");
  const DD = String(date.getDate()).padStart(2, "0");
  const HH = String(date.getHours()).padStart(2, "0");
  const mm = String(date.getMinutes()).padStart(2, "0");
  const ss = String(date.getSeconds()).padStart(2, "0");

  let result = formatPattern;
  result = result.replace(/YYYY/g, YYYY);
  result = result.replace(/MM/g, MM);
  result = result.replace(/DD/g, DD);
  result = result.replace(/HH/g, HH);
  result = result.replace(/mm/g, mm);
  result = result.replace(/ss/g, ss);
  return result;
}

/**
 * Shared date transformer. Default display: DD/MM/YYYY (e.g. 08/05/2026).
 * Invalid inputs keep the original text; callers can read warnings via formatDateDetailed.
 */
export function formatDate(val: unknown, formatPattern = "DD/MM/YYYY"): string {
  return formatDateDetailed(val, formatPattern).text;
}

export function formatDateDetailed(val: unknown, formatPattern = "DD/MM/YYYY"): FormatDateResult {
  if (val == null || val === "") return { text: "", invalid: false };

  const parsed = parseDateStrict(val);
  if (!parsed.date) {
    const original = String(val);
    return {
      text: original,
      invalid: parsed.keptOriginal,
      warning: parsed.reason,
    };
  }
  return { text: applyPattern(parsed.date, formatPattern), invalid: false };
}

export function formatNumber(val: unknown, decimals = 0): string {
  if (val == null || val === "") return "";
  const num = typeof val === "number" ? val : parseFloat(String(val).replace(/,/g, ""));
  if (isNaN(num)) return String(val);

  return num.toLocaleString("vi-VN", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

export function formatCurrency(val: unknown, decimals = 0, symbol = "₫"): string {
  if (val == null || val === "") return "";
  const numStr = formatNumber(val, decimals);
  if (!numStr || numStr === String(val)) return String(val);
  return `${numStr} ${symbol}`.trim();
}

export function formatPercent(val: unknown, decimals = 0): string {
  if (val == null || val === "") return "";
  let num = typeof val === "number" ? val : parseFloat(String(val));
  if (isNaN(num)) return String(val);

  if (Math.abs(num) <= 1 && num !== 0) {
    num = num * 100;
  }

  const formatted = num.toLocaleString("vi-VN", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  return `${formatted}%`;
}

/**
 * Format a cell for display. When `warnings` is provided, invalid date conversions
 * push a message instead of failing silently.
 *
 * Date instances and Excel serials are formatted as DD/MM/YYYY even when
 * formatType is omitted, so preview/PDF/print never show locale-dependent
 * short dates like `8/5/26`.
 */
export function formatFieldValue(
  val: unknown,
  config?: FieldFormattingConfig,
  warnings?: string[]
): string {
  const fallback = config?.fallback ?? "";

  if (val == null || val === "") {
    return fallback;
  }

  let formatted = String(val);
  const type = config?.formatType;

  const pushWarn = (msg: string) => {
    if (warnings && !warnings.includes(msg)) warnings.push(msg);
  };

  const asDate = (pattern: string) => {
    const result = formatDateDetailed(val, pattern);
    if (result.invalid && result.warning) pushWarn(result.warning);
    return result.text;
  };

  if (type === "date") {
    formatted = asDate(config?.dateFormat ?? "DD/MM/YYYY");
  } else if (type === "number") {
    formatted = formatNumber(val, config?.decimalPlaces ?? 0);
  } else if (type === "currency") {
    formatted = formatCurrency(val, config?.decimalPlaces ?? 0, config?.currencySymbol ?? "₫");
  } else if (type === "percent") {
    formatted = formatPercent(val, config?.decimalPlaces ?? 0);
  } else if (
    val instanceof Date ||
    (typeof val === "number" && looksLikeExcelSerial(val)) ||
    looksLikeDateString(val)
  ) {
    formatted = asDate(config?.dateFormat ?? "DD/MM/YYYY");
  } else {
    formatted = String(val);
  }

  if (config?.transform) {
    switch (config.transform) {
      case "uppercase":
        formatted = formatted.toUpperCase();
        break;
      case "lowercase":
        formatted = formatted.toLowerCase();
        break;
      case "trim":
        formatted = formatted.trim();
        break;
      case "none":
      default:
        break;
    }
  }

  if (!formatted.trim() && fallback) {
    return fallback;
  }

  return formatted;
}

/** Excel serial dates used in practice fall roughly in 2000–2100 (≈36526–73050). */
function looksLikeExcelSerial(val: number): boolean {
  return Number.isFinite(val) && val >= 20000 && val <= 80000 && Number.isInteger(val);
}

/** Display strings Excel leftover as m/d/yy or d/m/yyyy. */
function looksLikeDateString(val: unknown): boolean {
  if (typeof val !== "string") return false;
  return /^\d{1,2}[/.-]\d{1,2}[/.-](\d{2}|\d{4})$/.test(val.trim());
}
